import { ContentType } from '../../../lib/followUp';

export const PATIENT_LIBRARY_TYPE_PARAM = 'type';
export const PATIENT_LIBRARY_STATUS_PARAM = 'status';

export const PATIENT_LIBRARY_TYPE_SLUGS = [
  'action',
  'audio',
  'file',
  'form',
  'image',
  'link',
  'text',
] as const;

export const PATIENT_LIBRARY_STATUS_SLUGS = [
  'todo',
  'completed',
  'pending',
] as const;

export type PatientLibraryTypeSlug = typeof PATIENT_LIBRARY_TYPE_SLUGS[number];
export type PatientLibraryStatusFilter =
  typeof PATIENT_LIBRARY_STATUS_SLUGS[number];

export type PatientLibraryFilters = {
  type: PatientLibraryTypeSlug | null;
  status: PatientLibraryStatusFilter | null;
};

export const EMPTY_PATIENT_LIBRARY_FILTERS: PatientLibraryFilters = {
  type: null,
  status: null,
};

export const PATIENT_LIBRARY_STATUS_OPTIONS: Array<{
  label: string;
  value: PatientLibraryStatusFilter;
}> = [
  { label: 'To do', value: 'todo' },
  { label: 'Completed', value: 'completed' },
  { label: 'Pending', value: 'pending' },
];

const TYPE_SLUG_SET = new Set<string>(PATIENT_LIBRARY_TYPE_SLUGS);
const STATUS_SLUG_SET = new Set<string>(PATIENT_LIBRARY_STATUS_SLUGS);

const CONTENT_TYPE_TO_SLUG: Partial<
  Record<ContentType, PatientLibraryTypeSlug>
> = {
  [ContentType.Action]: 'action',
  [ContentType.Audio]: 'audio',
  [ContentType.PDF]: 'file',
  [ContentType.PdfForm]: 'file',
  [ContentType.Form]: 'form',
  [ContentType.Image]: 'image',
  [ContentType.Link]: 'link',
  [ContentType.Text]: 'text',
};

const SLUG_TO_CONTENT_TYPE: Record<PatientLibraryTypeSlug, ContentType> = {
  action: ContentType.Action,
  audio: ContentType.Audio,
  file: ContentType.PDF,
  form: ContentType.Form,
  image: ContentType.Image,
  link: ContentType.Link,
  text: ContentType.Text,
};

export const isPatientLibraryTypeSlug = (
  value: unknown,
): value is PatientLibraryTypeSlug =>
  typeof value === 'string' && TYPE_SLUG_SET.has(value);

export const isPatientLibraryStatusFilter = (
  value: unknown,
): value is PatientLibraryStatusFilter =>
  typeof value === 'string' && STATUS_SLUG_SET.has(value);

export const contentTypeToPatientLibraryTypeSlug = (
  contentType: ContentType | null,
): PatientLibraryTypeSlug | null => {
  if (!contentType) {
    return null;
  }
  return CONTENT_TYPE_TO_SLUG[contentType] ?? null;
};

export const patientLibraryTypeSlugToContentType = (
  slug: PatientLibraryTypeSlug | null,
): ContentType | null => {
  if (!slug) {
    return null;
  }
  return SLUG_TO_CONTENT_TYPE[slug];
};

export const urlHasPatientLibraryFilterKeys = (
  searchParams: URLSearchParams,
): boolean =>
  searchParams.has(PATIENT_LIBRARY_TYPE_PARAM) ||
  searchParams.has(PATIENT_LIBRARY_STATUS_PARAM);

export const parsePatientLibrarySearchParams = (
  searchParams: URLSearchParams,
): PatientLibraryFilters => {
  const typeValue = searchParams.get(PATIENT_LIBRARY_TYPE_PARAM);
  const statusValue = searchParams.get(PATIENT_LIBRARY_STATUS_PARAM);

  return {
    type: isPatientLibraryTypeSlug(typeValue) ? typeValue : null,
    status: isPatientLibraryStatusFilter(statusValue) ? statusValue : null,
  };
};

export const serializePatientLibrarySearchParams = (
  filters: PatientLibraryFilters,
  base?: URLSearchParams,
): URLSearchParams => {
  const next = new URLSearchParams(base);

  if (filters.type) {
    next.set(PATIENT_LIBRARY_TYPE_PARAM, filters.type);
  } else {
    next.delete(PATIENT_LIBRARY_TYPE_PARAM);
  }

  if (filters.status) {
    next.set(PATIENT_LIBRARY_STATUS_PARAM, filters.status);
  } else {
    next.delete(PATIENT_LIBRARY_STATUS_PARAM);
  }

  return next;
};

export const hasAnyPatientLibraryFilter = (
  filters: PatientLibraryFilters,
): boolean => Boolean(filters.type || filters.status);

export const patientLibraryFiltersEqual = (
  a: PatientLibraryFilters,
  b: PatientLibraryFilters,
): boolean => a.type === b.type && a.status === b.status;

export const normalizePatientLibraryFilters = (
  value: unknown,
): PatientLibraryFilters => {
  if (!value || typeof value !== 'object') {
    return EMPTY_PATIENT_LIBRARY_FILTERS;
  }

  const maybeFilters = value as Partial<PatientLibraryFilters>;

  return {
    type: isPatientLibraryTypeSlug(maybeFilters.type)
      ? maybeFilters.type
      : null,
    status: isPatientLibraryStatusFilter(maybeFilters.status)
      ? maybeFilters.status
      : null,
  };
};
